/**
 * views/FeatureEntitlementsView.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Platform-admin page for managing per-vendor feature entitlements.
 *
 * Layout:
 *   - Top: search box (debounced) + status / module filter dropdowns.
 *   - Table: one row per (vendor, module) entitlement. Columns: vendor,
 *     module, status badge, tier, expires, grantedAt, actions.
 *   - Per-row actions open a modal that collects { tier, expiresAt, reason }
 *     and dispatches the matching service call.
 *   - "Provision defaults" button on each vendor runs the bulk reset.
 *
 * Only SUPERUSER / ADMIN / SUPERVISOR users may interact with this page.
 * How each column and action works is in Settings/help/supportHelp.js
 * (1 Nov 2026); the copy here says only what the server does.
 * Non-admins are bounced by the backend with 403 ENTITLEMENT_ADMIN_REQUIRED,
 * and the EntitlementContext.bypassesEntitlements check here hides mutation
 * controls for anyone who shouldn't see them.
 *
 * @see controllers/featureEntitlementController.js
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Card,
  Container,
  Row,
  Col,
  Button,
  Form,
  Table,
  Modal,
  Spinner,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import { toast } from "react-toastify";
import {
  apiListAllEntitlements,
  apiGrantEntitlement,
  apiSuspendEntitlement,
  apiRevokeEntitlement,
  apiUpdateEntitlementTier,
  apiProvisionEntitlementDefaults,
} from "../api/index.js";
import { useEntitlement } from "../context/EntitlementContext.js";
import {
  FEATURE_MODULES,
  FEATURE_MODULE_LIST,
  MODULE_LABELS,
  MODULE_DESCRIPTIONS,
  ENTITLEMENT_TIERS,
  ENTITLEMENT_STATUS,
} from "../constants/featureModules.js";
import AppSelect from "../components/ui/AppSelect";
import SettingHelp from "./Settings/settingsHelp";
import { ReadOnlyNotice } from "./Settings/settingsUi";
import { AppButton } from "../components/ui";

const STATUS_OPTIONS = [
  "",
  ENTITLEMENT_STATUS.ACTIVE,
  ENTITLEMENT_STATUS.SUSPENDED,
  ENTITLEMENT_STATUS.REVOKED,
  ENTITLEMENT_STATUS.TRIAL,
  ENTITLEMENT_STATUS.EXPIRED,
];

// ── Status colour + visual spec ─────────────────────────────────────────────
const STATUS_META = {
  ACTIVE:          { bg: "#198754", label: "Active",          fg: "white" },
  TRIAL:           { bg: "#0dcaf0", label: "Trial",           fg: "#083344" },
  SUSPENDED:       { bg: "#ffc107", label: "Suspended",       fg: "#3b2800" },
  EXPIRED:         { bg: "#6c757d", label: "Expired",         fg: "white" },
  REVOKED:         { bg: "#dc3545", label: "Revoked",         fg: "white" },
  NOT_PROVISIONED: { bg: "#e9ecef", label: "Not provisioned", fg: "#495057" },
};

// ── Tier colour + visual spec ───────────────────────────────────────────────
// Gray → Blue → Purple: solid colours, no gradients (conveys increasing value).
const TIER_META = {
  BASIC:      { bg: "#6c757d", label: "Basic",      fg: "white" },
  PRO:        { bg: "#1a73e8", label: "Pro",        fg: "white" },
  ENTERPRISE: { bg: "#6b1a8a", label: "Enterprise", fg: "white" },
};

const StatusBadge = ({ status }) => {
  const m = STATUS_META[status] || {
    bg: "#adb5bd",
    label: status || "—",
    fg: "white",
  };
  return (
    <span
      style={{
        display: "inline-block",
        minWidth: 92,
        textAlign: "center",
        padding: "3px 10px",
        borderRadius: 12,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: "0.3px",
        textTransform: "uppercase",
        background: m.bg,
        color: m.fg,
      }}
    >
      {m.label}
    </span>
  );
};

const TierBadge = ({ tier }) => {
  const m = TIER_META[tier] || { bg: "#adb5bd", label: tier || "—", fg: "white" };
  return (
    <span
      style={{
        display: "inline-block",
        minWidth: 84,
        textAlign: "center",
        padding: "3px 10px",
        borderRadius: 12,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: "0.3px",
        textTransform: "uppercase",
        background: m.bg,
        color: m.fg,
      }}
    >
      {m.label}
    </span>
  );
};

// ── Clickable summary chip for the hero strip ───────────────────────────────
const SummaryChip = ({ label, count, color, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      padding: "6px 12px",
      borderRadius: 999,
      border: active ? `2px solid ${color}` : "1px solid #dee2e6",
      background: active ? `${color}14` : "white",
      color: "#1a2b4a",
      fontSize: 12,
      fontWeight: 600,
      letterSpacing: "0.2px",
      cursor: "pointer",
      transition: "all 0.15s",
    }}
  >
    <span
      style={{
        width: 8,
        height: 8,
        borderRadius: "50%",
        background: color,
        display: "inline-block",
      }}
    />
    {label}
    <span
      style={{
        background: active ? color : "#f1f3f5",
        color: active ? "white" : "#495057",
        borderRadius: 10,
        padding: "1px 8px",
        fontSize: 11,
        fontWeight: 700,
        minWidth: 22,
        textAlign: "center",
      }}
    >
      {count ?? 0}
    </span>
  </button>
);

// Debounce util — local to avoid an extra dep.
const useDebounced = (value, ms = 350) => {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
};

const FeatureEntitlementsView = ({ canEdit = false }) => {
  const { bypassesEntitlements, role, loading: entLoading } = useEntitlement();

  const [q, setQ] = useState("");
  const debouncedQ = useDebounced(q, 400);
  const [statusFilter, setStatusFilter] = useState("");
  const [moduleFilter, setModuleFilter] = useState("");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({ byStatus: {}, byTier: {} });
  const [tierFilter, setTierFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [skip, setSkip] = useState(0);
  const LIMIT = 50;

  // Mutation modal state
  const [modal, setModal] = useState(null); // { type, row } | null
  const [submitting, setSubmitting] = useState(false);
  const [formState, setFormState] = useState({
    tier: ENTITLEMENT_TIERS.BASIC,
    expiresAt: "",
    reason: "",
  });

  const [hasMore, setHasMore] = useState(false);

  const loadRows = useCallback(
    async (nextSkip = 0) => {
      setLoading(true);
      try {
        const params = {
          limit: LIMIT,
          skip: nextSkip,
        };
        if (statusFilter) params.status = statusFilter;
        if (moduleFilter) params.moduleKey = moduleFilter;
        if (debouncedQ && debouncedQ.trim().length >= 2) params.q = debouncedQ.trim();

        const { data } = await apiListAllEntitlements(params);
        setRows(data.rows || []);
        setTotal(data.total || 0);
        setCounts(data.counts || { byStatus: {}, byTier: {} });
        setHasMore(!!data.hasMore);
        setSkip(nextSkip);
      } catch (err) {
        toast.error(err?.response?.data?.message || "Failed to load entitlements.");
      } finally {
        setLoading(false);
      }
    },
    [debouncedQ, statusFilter, moduleFilter],
  );

  // Any filter change resets to page 1. Pagination stays on skip.
  useEffect(() => {
    loadRows(0);
  }, [debouncedQ, statusFilter, moduleFilter, tierFilter]); // eslint-disable-line

  // Client-side tier filter (cheap — tier is not a server-side filter yet).
  const visibleRows = useMemo(
    () => (tierFilter ? rows.filter((r) => r.tier === tierFilter) : rows),
    [rows, tierFilter],
  );

  const openModal = (type, row) => {
    setFormState({
      tier: row?.tier || ENTITLEMENT_TIERS.BASIC,
      expiresAt: row?.expiresAt
        ? new Date(row.expiresAt).toISOString().slice(0, 10)
        : "",
      reason: "",
    });
    setModal({ type, row });
  };

  const closeModal = () => {
    if (submitting) return;
    setModal(null);
  };

  const submitModal = async () => {
    if (!modal) return;
    const { type, row } = modal;
    const { tier, expiresAt, reason } = formState;
    const vendorId = row.vendorId;
    const moduleKey = row.moduleKey;

    const reasonRequired = type !== "grant" || !reason?.trim() === false;
    if (type !== "grant" && !reason.trim()) {
      toast.warn("Reason is required.");
      return;
    }

    try {
      setSubmitting(true);
      const payload = {
        moduleKey,
        tier,
        reason: reason.trim() || undefined,
        expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
      };
      if (type === "grant") await apiGrantEntitlement(vendorId, payload);
      else if (type === "suspend") await apiSuspendEntitlement(vendorId, payload);
      else if (type === "revoke") await apiRevokeEntitlement(vendorId, payload);
      else if (type === "tier") await apiUpdateEntitlementTier(vendorId, payload);

      toast.success(`Entitlement ${type} succeeded.`);
      setModal(null);
      loadRows(skip);
    } catch (err) {
      toast.error(
        err?.response?.data?.message || `Failed to ${type} entitlement.`,
      );
    } finally {
      setSubmitting(false);
    }
  };

  const submitReset = async () => {
    if (!modal || modal.type !== "reset") return;
    const reason = formState.reason.trim();
    if (!reason) {
      toast.warn("Reason is required.");
      return;
    }
    try {
      setSubmitting(true);
      await apiProvisionEntitlementDefaults(modal.row.vendorId, { reason });
      toast.success("Default entitlements provisioned.");
      setModal(null);
      loadRows(skip);
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Failed to provision defaults.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Non-admin fallback — this page should only render for platform admins.
  if (!entLoading && !bypassesEntitlements) {
    return (
      <Container style={{ paddingTop: 32 }}>
        <Card>
          <Card.Body>
            <Card.Title>Restricted</Card.Title>
            <p>This page is only available to platform administrators.</p>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  const totalInScope = Object.values(counts.byStatus || {}).reduce(
    (a, n) => a + n,
    0,
  );
  const anyFilterActive = !!(statusFilter || moduleFilter || tierFilter || q);

  return (
    <Container fluid style={{ paddingTop: 16 }}>
      {!canEdit && <ReadOnlyNotice level="update" />}
      {/* Hero */}
      <div
        style={{
          background:
            "linear-gradient(135deg, #1a2b4a 0%, #2a4373 55%, #17385a 100%)",
          color: "white",
          borderRadius: 12,
          padding: "24px 28px",
          marginBottom: 18,
          boxShadow: "0 6px 24px rgba(26,43,74,0.22)",
        }}
      >
        <div
          style={{
            fontSize: 11,
            opacity: 0.8,
            letterSpacing: "1.2px",
            fontWeight: 600,
          }}
        >
          VENDOR AUDIT TOOLS
        </div>
        <h2 style={{ fontWeight: 800, margin: "2px 0 6px", fontSize: 26 }}>
          Feature Entitlements
          <SettingHelp topic="featureEntitlements" placement="bottom" />
        </h2>
        <div style={{ fontSize: 13, opacity: 0.9 }}>
          Decide which of the {FEATURE_MODULE_LIST.length} audit tools each
          vendor's team members may use. The vendor's owner always keeps every
          tool. Signed in as <strong>{role || "ADMIN"}</strong>. {totalInScope}{" "}
          entitlement{totalInScope === 1 ? "" : "s"} in current scope.
        </div>
      </div>

      {/* Summary chips */}
      <Card className="shadow-sm mb-3">
        <Card.Body style={{ paddingTop: 14, paddingBottom: 14 }}>
          <div
            style={{
              fontSize: 11,
              color: "#6b7a99",
              fontWeight: 700,
              letterSpacing: "0.5px",
              marginBottom: 6,
            }}
          >
            BY STATUS
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {Object.keys(STATUS_META).map((s) => (
              <SummaryChip
                key={s}
                label={STATUS_META[s].label}
                count={counts.byStatus?.[s] || 0}
                color={STATUS_META[s].bg}
                active={statusFilter === s}
                onClick={() => setStatusFilter(statusFilter === s ? "" : s)}
              />
            ))}
          </div>

          <div
            style={{
              fontSize: 11,
              color: "#6b7a99",
              fontWeight: 700,
              letterSpacing: "0.5px",
              marginTop: 14,
              marginBottom: 6,
            }}
          >
            BY TIER
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {Object.keys(TIER_META).map((t) => (
              <SummaryChip
                key={t}
                label={TIER_META[t].label}
                count={counts.byTier?.[t] || 0}
                color={TIER_META[t].bg}
                active={tierFilter === t}
                onClick={() => setTierFilter(tierFilter === t ? "" : t)}
              />
            ))}
            {anyFilterActive && (
              <button
                type="button"
                onClick={() => {
                  setStatusFilter("");
                  setTierFilter("");
                  setModuleFilter("");
                  setQ("");
                }}
                style={{
                  marginLeft: "auto",
                  background: "transparent",
                  border: "none",
                  color: "#0d6efd",
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Clear all filters
              </button>
            )}
          </div>
        </Card.Body>
      </Card>

      <Card className="shadow-sm">
        <Card.Header style={{ background: "white", borderBottom: "1px solid #eef1f5" }}>
          <Row className="align-items-center g-2">
            <Col md={5}>
              <Form.Control
                size="sm"
                type="text"
                placeholder="Search vendor by name / email…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </Col>
            <Col md={3}>
              <AppSelect block
                size="sm"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {s ? STATUS_META[s]?.label || s : "All statuses"}
                  </option>
                ))}
              </AppSelect>
            </Col>
            <Col md={4}>
              <AppSelect block
                size="sm"
                value={moduleFilter}
                onChange={(e) => setModuleFilter(e.target.value)}
              >
                <option value="">All modules</option>
                {FEATURE_MODULE_LIST.map((m) => (
                  <option key={m} value={m}>
                    {MODULE_LABELS[m] || m}
                  </option>
                ))}
              </AppSelect>
            </Col>
          </Row>
        </Card.Header>
        <Card.Body style={{ paddingTop: 0 }}>
          {loading ? (
            <div style={{ textAlign: "center", padding: 40 }}>
              <Spinner animation="border" />
            </div>
          ) : visibleRows.length === 0 ? (
            <div style={{ textAlign: "center", padding: 40, color: "#888" }}>
              No entitlement rows match your filters.
            </div>
          ) : (
            <Table responsive hover className="table-sm align-middle">
              <thead style={{ background: "#f7f9fc" }}>
                <tr>
                  <th>Vendor</th>
                  <th>Module<SettingHelp topic="entitlementModule" /></th>
                  <th>Status<SettingHelp topic="entitlementStatus" /></th>
                  <th>Tier<SettingHelp topic="entitlementTier" /></th>
                  <th>Expires<SettingHelp topic="entitlementExpiry" /></th>
                  <th>Granted</th>
                  <th style={{ width: 260, textAlign: "right" }}>Actions<SettingHelp topic="entitlementGrantSuspendRevoke" /></th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((row) => (
                  <tr key={row._id}>
                    <td>
                      <div style={{ fontWeight: 600, color: "#1a2b4a" }}>
                        {row.vendor?.businessName ||
                          row.vendor?.name ||
                          row.vendor?._id ||
                          row.vendorId}
                      </div>
                      <div style={{ fontSize: 12, color: "#868e96" }}>
                        {row.vendor?.email || ""}{" "}
                        {row.vendor?.operationMode
                          ? `· ${row.vendor.operationMode}`
                          : ""}
                      </div>
                    </td>
                    <td>
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip>
                            {MODULE_DESCRIPTIONS[row.moduleKey] ||
                              "Module description unavailable."}
                          </Tooltip>
                        }
                      >
                        <span
                          style={{
                            borderBottom: "1px dotted #adb5bd",
                            cursor: "help",
                          }}
                        >
                          {MODULE_LABELS[row.moduleKey] || row.moduleKey}
                        </span>
                      </OverlayTrigger>
                    </td>
                    <td>
                      <StatusBadge status={row.status} />
                    </td>
                    <td>
                      <TierBadge tier={row.tier} />
                    </td>
                    <td style={{ fontSize: 12, color: "#666" }}>
                      {row.expiresAt
                        ? new Date(row.expiresAt).toLocaleDateString()
                        : "—"}
                    </td>
                    <td style={{ fontSize: 12, color: "#666" }}>
                      {row.grantedAt
                        ? new Date(row.grantedAt).toLocaleDateString()
                        : "—"}
                    </td>
                    <td style={{ textAlign: "right" }}>
                      {/* Changing a row needs the settings update level, the
                          rule the server applies to these five actions. */}
                      {canEdit ? (
                        <div style={{ display: "inline-flex", gap: 4, flexWrap: "wrap", justifyContent: "flex-end" }}>
                          <AppButton
                            size="sm"
                            onClick={() => openModal("grant", row)}
                            title="Turn this tool on for the vendor's team, lifting any suspension or revocation"
                          >
                            Grant
                          </AppButton>
                          <AppButton
                            size="sm"
                            variant="quiet"
                            onClick={() => openModal("tier", row)}
                            title="Change the tier label. No limit is applied by tier yet"
                          >
                            Tier
                          </AppButton>
                          <AppButton
                            size="sm"
                            variant="warning"
                            onClick={() => openModal("suspend", row)}
                            title="Stop the vendor's team using this tool until it is granted again"
                          >
                            Suspend
                          </AppButton>
                          <AppButton
                            size="sm"
                            variant="danger"
                            onClick={() => openModal("revoke", row)}
                            title="Withdraw this tool from the vendor's team. Grant undoes it"
                          >
                            Revoke
                          </AppButton>
                          <AppButton
                            size="sm"
                            variant="quiet"
                            onClick={() => openModal("reset", row)}
                            title={`Turn the ${FEATURE_MODULE_LIST.length} audit tools back on at Basic where they are missing, suspended or revoked`}
                          >
                            Reset
                          </AppButton>
                        </div>
                      ) : (
                        <span style={{ fontSize: 12, color: "#888" }}>View only</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
          {/* ── Pagination + row count ──────────────────────────────────── */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              paddingTop: 12,
              borderTop: "1px solid #eef1f5",
              marginTop: 8,
              flexWrap: "wrap",
              gap: 8,
            }}
          >
            <div style={{ color: "#6c757d", fontSize: 12 }}>
              {total === 0 ? (
                "No rows"
              ) : (
                <>
                  Showing{" "}
                  <strong>
                    {skip + 1}–{skip + visibleRows.length}
                  </strong>{" "}
                  of <strong>{total}</strong> row{total === 1 ? "" : "s"}
                  {tierFilter && (
                    <span className="ms-2 text-muted">
                      (tier filter applied to this page)
                    </span>
                  )}
                </>
              )}
            </div>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              <AppButton
                size="sm"
                variant="quiet"
                disabled={loading || skip === 0}
                onClick={() => loadRows(Math.max(0, skip - LIMIT))}
              >
                Previous
              </AppButton>
              <span style={{ fontSize: 12, color: "#666", minWidth: 90, textAlign: "center" }}>
                Page {Math.floor(skip / LIMIT) + 1}
                {total > 0 && ` of ${Math.max(1, Math.ceil(total / LIMIT))}`}
              </span>
              <AppButton
                size="sm"
                variant="quiet"
                disabled={loading || !hasMore}
                onClick={() => loadRows(skip + LIMIT)}
              >
                Next
              </AppButton>
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* ── Mutation modal ──────────────────────────────────── */}
      <Modal show={!!modal} onHide={closeModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {modal?.type === "grant" && "Grant / re-grant entitlement"}
            {modal?.type === "suspend" && "Suspend entitlement"}
            {modal?.type === "revoke" && "Revoke entitlement"}
            {modal?.type === "tier" && "Change tier"}
            {modal?.type === "reset" && "Reset audit tools to defaults"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modal && (
            <>
              {modal.type === "reset" ? (
                <p style={{ marginBottom: 16 }}>
                  For{" "}
                  <strong>
                    {modal.row.vendor?.businessName ||
                      modal.row.vendor?.name ||
                      modal.row.vendorId}
                  </strong>
                  , every one of the {FEATURE_MODULE_LIST.length} audit tools
                  that is missing, suspended or revoked is turned back on at the{" "}
                  <TierBadge tier="BASIC" /> tier. A tool that is already active
                  keeps its tier and expiry date, so an upgrade is not undone and
                  an expired grant stays expired. URA EFRIS filing is never
                  touched.
                  <SettingHelp topic="entitlementReset" />
                </p>
              ) : (
                <p style={{ marginBottom: 16 }}>
                  <strong>{MODULE_LABELS[modal.row.moduleKey]}</strong> for{" "}
                  <strong>
                    {modal.row.vendor?.businessName ||
                      modal.row.vendor?.name ||
                      modal.row.vendorId}
                  </strong>
                </p>
              )}
              {(modal.type === "grant" || modal.type === "tier") && (
                <Form.Group className="mb-3">
                  <Form.Label>Tier<SettingHelp topic="entitlementTier" /></Form.Label>
                  <AppSelect block
                    value={formState.tier}
                    onChange={(e) =>
                      setFormState((s) => ({ ...s, tier: e.target.value }))
                    }
                  >
                    {Object.values(ENTITLEMENT_TIERS).map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </AppSelect>
                </Form.Group>
              )}
              {modal.type === "grant" && (
                <Form.Group className="mb-3">
                  <Form.Label>Expires on (optional)<SettingHelp topic="entitlementExpiry" /></Form.Label>
                  <Form.Control
                    type="date"
                    value={formState.expiresAt}
                    onChange={(e) =>
                      setFormState((s) => ({
                        ...s,
                        expiresAt: e.target.value,
                      }))
                    }
                  />
                  <Form.Text className="text-muted">
                    Leave blank to keep it on until someone suspends or revokes
                    it. From this date the vendor's team loses the tool; the
                    owner keeps it.
                  </Form.Text>
                </Form.Group>
              )}
              <Form.Group className="mb-3">
                <Form.Label>
                  Reason {modal.type !== "grant" && <span style={{ color: "red" }}>*</span>}
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={formState.reason}
                  placeholder={
                    modal.type === "grant"
                      ? "Optional, for example Paid upgrade or Pilot customer. Recorded in the vendor's activity feed."
                      : "Required. Why, for example billing, policy or an investigation. Recorded in the vendor's activity feed."
                  }
                  onChange={(e) =>
                    setFormState((s) => ({ ...s, reason: e.target.value }))
                  }
                />
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeModal} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant={
              modal?.type === "revoke"
                ? "danger"
                : modal?.type === "suspend"
                ? "warning"
                : modal?.type === "reset"
                ? "secondary"
                : "primary"
            }
            onClick={modal?.type === "reset" ? submitReset : submitModal}
            disabled={submitting}
          >
            {submitting ? "Working…" : `Confirm ${modal?.type || ""}`}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default FeatureEntitlementsView;
